import React from 'react';
import { SmartphoneIcon } from 'lucide-react';
import { useLaunch } from '../contexts/LaunchContext';
import { BadgeCrop, STORE_BADGE_ASSETS, STORE_BADGE_CROPS } from '../data/launch';
type Tone = 'light' | 'dark';

/** Rendered height of a store badge, matching the 56px button height. */
const BADGE_HEIGHT = 52;

/**
 * Renders official badge artwork at a consistent height.
 *
 * Official downloads are often supplied on an oversized square canvas with
 * transparent padding. When a crop is declared for the asset, the artwork is
 * scaled and clipped to that region so the badge fills the button height and
 * the link's hit area matches what you can see.
 */
function BadgeImage({
  src,
  alt,
  crop
}: {
  src: string;
  alt: string;
  crop: BadgeCrop | null;
}) {
  if (!crop) {
    return <img src={src} alt={alt} style={{
      height: BADGE_HEIGHT
    }} className="block w-auto" data-id="element-323" data-mp="33leq6b311i7h" />;
  }

  // Scale the whole image up so the cropped band equals BADGE_HEIGHT.
  const scaledSize = BADGE_HEIGHT / crop.height;
  return <span className="relative block overflow-hidden" style={{
    height: BADGE_HEIGHT,
    width: scaledSize * crop.width
  }} data-id="element-324" data-mp="38cpfkfruki2s">
      <img src={src} alt={alt} className="absolute max-w-none" style={{
      width: scaledSize,
      height: scaledSize,
      left: -scaledSize * crop.left,
      top: -scaledSize * crop.top
    }} data-id="element-325" data-mp="33e4ao31nha80" />
    </span>;
}

/**
 * App Store / Google Play download links.
 *
 * Only stores with a real URL are rendered, so the site never shows a dead or
 * disabled download button. While one platform is still pending, a short note
 * explains it and points people at browser join instead.
 *
 * Apple and Google both require their OFFICIAL badge artwork, used to their
 * brand guidelines. Home-made lookalikes are a guideline breach, so a badge
 * image is only rendered once the official asset is set in STORE_BADGE_ASSETS.
 * Until then a plain text button is used.
 */
function StoreLink({
  href,
  badgeSrc,
  badgeCrop,
  badgeAlt,
  label,
  tone,
  emphasis
}: {
  href: string;
  badgeSrc: string;
  badgeCrop: BadgeCrop | null;
  badgeAlt: string;
  label: string;
  tone: Tone;
  emphasis: 'primary' | 'secondary';
}) {
  if (badgeSrc) {
    return <a href={href} target="_blank" rel="noopener noreferrer" className="inline-block rounded-lg transition-opacity duration-150 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow focus-visible:ring-offset-2" data-id="element-326" data-mp="stexwcb40g2y">
        <BadgeImage src={badgeSrc} alt={badgeAlt} crop={badgeCrop} data-id="element-327" data-mp="53km7y2j0xka" />
      </a>;
  }
  const base = 'inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 min-h-[56px] text-base font-bold transition-colors duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2';
  const skins = {
    light: {
      primary: 'bg-kitty-dark text-white hover:bg-black focus-visible:ring-kitty-yellow focus-visible:ring-offset-white',
      secondary: 'bg-white text-kitty-dark border border-gray-200 hover:bg-gray-50 focus-visible:ring-kitty-yellow focus-visible:ring-offset-white'
    },
    dark: {
      primary: 'bg-white text-kitty-dark hover:bg-gray-100 focus-visible:ring-kitty-yellow focus-visible:ring-offset-gray-950',
      secondary: 'bg-white/[0.08] text-white border border-white/20 hover:bg-white/[0.14] focus-visible:ring-kitty-yellow focus-visible:ring-offset-gray-950'
    }
  };
  return <a href={href} target="_blank" rel="noopener noreferrer" className={`${base} ${skins[tone][emphasis]}`} data-id="element-328" data-mp="950oiy5dfva2">
      <SmartphoneIcon className="w-5 h-5 flex-shrink-0" aria-hidden="true" data-id="element-329" data-mp="j3pvvwzn5xc2" />
      {label}
    </a>;
}
export function StoreBadges({
  tone = 'light',
  align = 'start'
}: {
  tone?: Tone;
  align?: 'start' | 'center';
}) {
  const {
    appStoreUrl,
    googlePlayUrl,
    isAppStoreLive,
    isGooglePlayLive
  } = useLaunch();

  // Solid kitty-dark passes contrast on white and on the brand yellow; the
  // dark tone sits on gray-950, where gray-300 is comfortably above 4.5:1.
  const noteTone = tone === 'dark' ? 'text-gray-300' : 'text-kitty-dark';
  const alignment = align === 'center' ? 'items-center' : 'items-start';
  return <div className={`flex flex-col gap-3 ${alignment}`} data-id="element-330" data-mp="1op9o84pj3jzr">
      <div className={`flex flex-wrap gap-3 ${align === 'center' ? 'justify-center' : ''}`} data-id="element-331" data-mp="1oqugfnodequg">
        {isGooglePlayLive && <StoreLink href={googlePlayUrl} badgeSrc={STORE_BADGE_ASSETS.googlePlayBadgeSrc} badgeCrop={STORE_BADGE_CROPS.googlePlay} badgeAlt="Get KittyPot on Google Play" label="Free on Google Play" tone={tone} emphasis="primary" data-id="element-332" data-mp="1yluusuh7535p" />}
        {isAppStoreLive && <StoreLink href={appStoreUrl} badgeSrc={STORE_BADGE_ASSETS.appStoreBadgeSrc} badgeCrop={STORE_BADGE_CROPS.appStore} badgeAlt="Download KittyPot on the App Store" label="Free on the App Store" tone={tone} emphasis={isGooglePlayLive ? 'secondary' : 'primary'} data-id="element-333" data-mp="13e2asf264fle" />}
      </div>

      {!isAppStoreLive && isGooglePlayLive && <p className={`text-sm ${noteTone} ${align === 'center' ? 'text-center' : ''}`} data-id="element-334" data-mp="jqctj1ruxxqf">
          On an iPhone? The iOS app is on its way, you can still join a kitty
          from your browser today.
        </p>}
    </div>;
}